import { Image, Page, Text, View } from "@react-pdf/renderer";
import { sortProductSizes } from "utils/UtilityFuncs";
import ShipmentPdfHeader from "./ShipmentPdfHeader";
import ShipmentPdfInfo from "./ShipmentPdfInfo";

export default function ShipmentPdfProducts({
  styles,
  locationLogo,
  locationInfo,
  logo,
  shipment,
  productsList,
}) {
  /*   All States
   ********************************************* */

  /*   All Functions
   ********************************************* */
  const limitFirstPage = 5; // Limit for the first page
  const limitOtherPages = 6; // Limit for subsequent pages

  const getTotalPages = (list) => {
    if (!list || list.length === 0) return 0;

    const firstPageItems = Math.min(list.length, limitFirstPage);
    const remainingItems = list.length - firstPageItems;

    return 1 + Math.ceil(remainingItems / limitOtherPages);
  };

  // Parses productSizes AND puts it in the order the work order defines --
  // the stored array is insertion order, which is not the same thing.
  const parseSizes = (sizes) => sortProductSizes(sizes);

  /*   All UseEffects
   ********************************************* */

  return (
    <>
      {productsList &&
        Array.from({ length: getTotalPages(productsList) }, (_, pagesIndex) => {
          let sliceStart, sliceEnd;

          if (pagesIndex === 0) {
            sliceStart = 0;
            sliceEnd = limitFirstPage;
          } else {
            sliceStart = limitFirstPage + (pagesIndex - 1) * limitOtherPages;
            sliceEnd = sliceStart + limitOtherPages;
          }

          const itemsInPage = productsList.slice(sliceStart, sliceEnd);

          return (
            <Page key={pagesIndex} size="LETTER" style={styles.page}>
              {pagesIndex === 0 && (
                <ShipmentPdfHeader
                  locationLogo={locationLogo}
                  locationInfo={locationInfo}
                  logo={logo}
                  styles={styles}
                  shipment={shipment}
                />
              )}

              {/*   Shipment Info (Customer, Shipping Address, Tracking, Courier)
               ********************************************* */}
              {pagesIndex === 0 && (
                <ShipmentPdfInfo styles={styles} shipment={shipment} />
              )}
              {/*   ALL Product Details
               ********************************************* */}
              <View style={[styles.productDetails, { marginTop: 10 }]}>
                {/* ----------  Looping Products   ---------- */}
                {itemsInPage.map((item, index) => {
                  const sizes = parseSizes(item.productSizes);

                  // Calculate total shipped and pending quantities
                  const totalShippedQty = sizes.reduce(
                    (acc, size) => acc + (Number(size.shipmentQty) || 0),
                    0
                  );
                  const totalQty = sizes.reduce(
                    (acc, size) => acc + (Number(size.quantity) || 0),
                    0
                  );
                  const totalPendingQty = totalQty - totalShippedQty;

                  // Determine shipment status
                  const shipmentStatus =
                    totalShippedQty === 0
                      ? "Pending"
                      : totalShippedQty >= totalQty
                      ? "Fully Shipped"
                      : "Partially Shipped";

                  // Determine background color based on status
                  const backgroundColor =
                    shipmentStatus === "Fully Shipped"
                      ? "#d1f0d1" // Light green (success with opacity)
                      : shipmentStatus === "Partially Shipped"
                      ? "#fff8dc" // Light yellow (warning with opacity)
                      : "#f5f5f5"; // Light gray (secondary)

                  return (
                    <View
                      key={item?.id}
                      style={{
                        flexDirection: "row",
                        border: "1px solid #dee2e6",
                        borderRadius: 6,
                        marginBottom: 6,
                        padding: 8,
                        alignItems: "center",
                        backgroundColor: backgroundColor,
                      }}
                    >
                      {/* Product Image */}
                      <View
                        style={{
                          width: 120,
                          height: 90,
                          marginRight: 12,
                          backgroundColor: "#f5f5f5",
                          borderRadius: 4,
                          overflow: "hidden",
                        }}
                      >
                        {(item?.productImage ||
                          item?.WOProduct?.productImage) && (
                          <Image
                            style={{
                              width: "100%",
                              height: "100%",
                              objectFit: "cover",
                            }}
                            src={
                              item?.productImage ||
                              item?.WOProduct?.productImage
                            }
                          />
                        )}
                      </View>

                      {/* Product Info */}
                      <View style={{ flex: 1, paddingRight: 8 }}>
                        {/* First Row: Product Type - Style ID, Color, and Status Badge */}
                        <View
                          style={{
                            flexDirection: "row",
                            marginBottom: 4,
                            alignItems: "center",
                            justifyContent: "space-between",
                          }}
                        >
                          {/* Product Type - Style ID */}
                          <Text
                            style={{
                              fontSize: 10,
                              fontWeight: "bold",
                            }}
                          >
                            {item?.productTypes?.productTypes ||
                              item?.WOProduct?.productTypes?.productTypes ||
                              "Product Type"}{" "}
                            -{" "}
                            {item?.productStyle ||
                              item?.WOProduct?.productStyle ||
                              "Style ID"}
                          </Text>

                          {/* Product Color */}
                          <Text
                            style={{
                              fontSize: 10,
                              marginHorizontal: 12,
                            }}
                          >
                            {item?.productColor ||
                              item?.WOProduct?.productColor ||
                              ""}
                          </Text>

                          {/* Status Badge */}
                          <View
                            style={{
                              backgroundColor:
                                shipmentStatus === "Fully Shipped"
                                  ? "#28a745"
                                  : shipmentStatus === "Partially Shipped"
                                  ? "#ffc107"
                                  : "#6c757d",
                              borderRadius: 12,
                              paddingHorizontal: 10,
                              paddingVertical: 4,
                            }}
                          >
                            <Text
                              style={{
                                fontSize: 10,
                                fontWeight: "bold",
                                color: "#fff",
                              }}
                            >
                              {shipmentStatus}
                            </Text>
                          </View>
                        </View>

                        {/* Second Row: WO ID and Quantities */}
                        <View
                          style={{
                            flexDirection: "row",
                            marginBottom: 6,
                            alignItems: "start",
                            justifyContent: "space-between",
                          }}
                        >
                          {/* WO ID */}
                          <Text
                            style={{
                              fontSize: 10,
                              color: "#999",
                            }}
                          >
                            W-{item?.woId || "ID"}
                          </Text>

                          {/* Quantities */}
                          <View style={{ flexDirection: "row", gap: 12 }}>
                            <Text
                              style={{
                                fontSize: 10,
                                fontWeight: "bold",
                              }}
                            >
                              Total Shipped : {totalShippedQty}
                            </Text>
                            <Text
                              style={{
                                fontSize: 10,
                                fontWeight: "bold",
                              }}
                            >
                              Total Pending : {totalPendingQty}
                            </Text>
                          </View>
                        </View>

                        {/* Third Row: Shipped label */}
                        {totalShippedQty > 0 && (
                          <Text
                            style={{
                              fontSize: 10,
                              fontWeight: "bold",
                              marginBottom: 3,
                            }}
                          >
                            Shipped:
                          </Text>
                        )}

                        {/* Size badges - Shipped */}
                        <View
                          style={{
                            flexDirection: "row",
                            flexWrap: "wrap",
                            gap: 4,
                            marginBottom: 6,
                          }}
                        >
                          {sizes.map((size, idx) => {
                            const shippedQty = Number(size.shipmentQty) || 0;

                            // Only show sizes that have shipped quantity
                            if (shippedQty === 0) return null;

                            return (
                              <View
                                key={idx}
                                style={{
                                  border: "1px solid #6c757d",
                                  borderRadius: 12,
                                  paddingHorizontal: 8,
                                  paddingVertical: 3,
                                  backgroundColor: "#fff",
                                }}
                              >
                                <Text style={{ fontSize: 9 }}>
                                  {size?.sizeName || size?.size}: {shippedQty}
                                </Text>
                              </View>
                            );
                          })}
                        </View>

                        {/* Pending section - only show if there are pending quantities */}
                        {totalPendingQty > 0 && (
                          <>
                            <Text
                              style={{
                                fontSize: 10,
                                fontWeight: "bold",
                                marginBottom: 3,
                              }}
                            >
                              Pending:
                            </Text>
                            <View
                              style={{
                                flexDirection: "row",
                                flexWrap: "wrap",
                                gap: 4,
                              }}
                            >
                              {sizes.map((size, idx) => {
                                const shippedQty =
                                  Number(size.shipmentQty) || 0;
                                const totalQty = Number(size.quantity) || 0;
                                const pendingQty = totalQty - shippedQty;

                                // Only show sizes that have pending quantity
                                if (pendingQty === 0) return null;

                                return (
                                  <View
                                    key={idx}
                                    style={{
                                      border: "1px solid #6c757d",
                                      borderRadius: 12,
                                      paddingHorizontal: 8,
                                      paddingVertical: 3,
                                      backgroundColor: "#fff",
                                    }}
                                  >
                                    <Text style={{ fontSize: 9 }}>
                                      {size?.sizeName || size?.size}:{" "}
                                      {pendingQty}
                                    </Text>
                                  </View>
                                );
                              })}
                            </View>
                          </>
                        )}
                      </View>
                    </View>
                  );
                })}
              </View>
            </Page>
          );
        })}
    </>
  );
}
